// Business Control Center route tree (Blueprint Wave D / D-2).
//
// Mounts the operator surface at /control/* inside the dedicated OperatorShell
// (NOT the firm Shell). Each destination is its own deep-linkable URL — replacing
// the COMMAND/TENANTS/INSIGHTS content-pills and the 22-entry section registry
// (D-26). Page bodies are progressively wired to the existing BCC callables in
// D-3…D-6; this module establishes the IA, navigation, and shell.

import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { OperatorShell, type PlatformRole } from "../../components/control";
import { ControlDashboardPage } from "./pages/ControlDashboardPage";
import { ControlTenantsPage } from "./pages/ControlTenantsPage";
import { ControlTenant360Page } from "./pages/ControlTenant360Page";
import { ControlOfferBuilderPage } from "./pages/ControlOfferBuilderPage";
import {
  ControlAiCreditsPage,
  ControlAuditPage,
  ControlBillingPage,
  ControlOffersPage,
  ControlPlatformHealthPage,
  ControlRevenuePage,
  ControlSettingsPage,
  ControlSupportPage,
  ControlWorkQueuePage,
} from "./pages/ControlOperationsPages";

export function ControlCenterRoutes() {
  const { user, isPlatformOwner, isSuperAdmin, platformRole: platformRoleClaim, signOut } = useAuth();
  const navigate = useNavigate();

  // Operator access guard (S-1). Any user carrying a platformRole claim — or the
  // platform owner / super admin — is an operator; everyone else is bounced.
  const hasOperatorAccess = Boolean(platformRoleClaim) || isPlatformOwner || isSuperAdmin;
  if (!user) {
    return <Navigate to="/login" replace state={{ from: { pathname: "/control/dashboard" } }} />;
  }

  if (!hasOperatorAccess) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-100 p-6 text-center">
        <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-lg font-black text-slate-950">Operator Access Required</h1>
          <p className="mt-2 text-sm text-slate-500">This deployment is restricted to platform operators.</p>
          <button type="button" onClick={() => void signOut()} className="mt-4 rounded-xl bg-slate-950 px-4 py-2 text-sm font-bold text-white">
            Sign out
          </button>
        </div>
      </div>
    );
  }

  const OPERATOR_ROLES: PlatformRole[] = [
    "platform_owner",
    "business_operator",
    "billing_operator",
    "support_operator",
    "finance_reviewer",
    "readonly_auditor",
  ];
  const platformRole: PlatformRole = OPERATOR_ROLES.includes(platformRoleClaim as PlatformRole)
    ? (platformRoleClaim as PlatformRole)
    : "platform_owner";
  const operator = {
    name: user.displayName || user.email || "Operator",
    email: user.email || undefined,
    platformRole,
  };

  return (
    <OperatorShell operator={operator} onSignOut={() => void signOut()}>
      <Routes>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<ControlDashboardPage />} />
        <Route path="queue" element={<ControlWorkQueuePage />} />
        <Route path="tenants" element={<ControlTenantsPage />} />
        <Route path="tenants/:firmId" element={<ControlTenant360Page />} />
        <Route path="offers" element={<ControlOffersPage />} />
        <Route path="offers/new" element={<ControlOfferBuilderPage />} />
        <Route path="revenue" element={<ControlRevenuePage />} />
        <Route path="billing" element={<ControlBillingPage />} />
        <Route path="ai-credits" element={<ControlAiCreditsPage />} />
        <Route path="platform-health" element={<ControlPlatformHealthPage />} />
        <Route path="support" element={<ControlSupportPage />} />
        <Route path="audit" element={<ControlAuditPage />} />
        <Route path="settings" element={<ControlSettingsPage />} />
        <Route path="*" element={<button type="button" onClick={() => navigate("/control/dashboard")} className="text-sm font-bold text-orange-600">Go to Dashboard</button>} />
      </Routes>
    </OperatorShell>
  );
}

export default ControlCenterRoutes;
